/* eslint-disable */
import { ReactElement, useState, useEffect, useMemo, memo, type CSSProperties } from "react"
import { isMobile } from "react-device-detect"
import { usePartner } from "../utils/partner"
import { IS_DEV } from "../data/js/server-constants"
import { SellwildBannerSize } from "../utils/native/sellwild-plugin"
import { useAdSlot } from "./ad-slot-budget"
import { useAdInsertRelease } from "./ad-insert-batch"
import { scheduleAdSlotReap } from "./ad-slot-reaper"
import { wantsDesktopAd } from "../utils/ad-layout"

export interface AdFrameworkProps {
    bidstream?: {
        mobile?: ReactElement
        desktop?: ReactElement
    }
    adsense?: ReactElement
    /**
     * Render nothing on a viewport narrower than this. For placements that CSS
     * hides at small widths: a mounted <sellwild-ad> still defines a GAM slot
     * and requests an impression even when `display: none` means nobody sees it.
     */
    minViewportWidth?: number
}

/**
 * The sidebar column (`.sidebar-ad`, styles/components/listing.scss) is
 * `display: none` up to `$mobile-width` (959px), so its ad needs at least 960.
 */
export const SIDEBAR_AD_MIN_VIEWPORT_WIDTH = 960

const bidstreamPartnerSet = new Set(['siteimpact', ''])

const SUPPORTED_SIZES = new Set<SellwildBannerSize>(['320x50', '300x250', '728x90', '300x600', '160x600'])

function extractAdMeta(el: ReactElement | undefined): { size: SellwildBannerSize; width: number; height: number } | null {
    if (!el) return null
    const props = el.props as Record<string, unknown>
    const name = typeof props['data-name'] === 'string' ? (props['data-name'] as string) : ''
    const dim = name.match(/(\d+)x(\d+)/)
    if (!dim) return null
    const width = Number(dim[1])
    const height = Number(dim[2])
    const sizeKey = `${width}x${height}` as SellwildBannerSize
    if (!SUPPORTED_SIZES.has(sizeKey)) return null
    return { size: sizeKey, width, height }
}

// Renders ads via the web widget (<sellwild-ad>) inside the WebView on every
// platform — including Capacitor iOS/Android. The native UIView overlay path
// was removed because it z-indexes above sticky DOM headers and can't sync
// position cleanly across the JS<->native bridge during scroll. Native
// IDFA/ATT signals are still piped to the widget via window.__SELLWILD_APP__
// (initialized in _document.tsx, populated by SellwildPlugin at runtime).
const AdFrameworkInner = (props: AdFrameworkProps) => {
    const { partner } = usePartner()
    const [mounted, setMounted] = useState(false)
    // Until mount there is no viewport; the user agent is the only guess, and
    // nothing is inserted before mount anyway (`released` below).
    const [desktop, setDesktop] = useState(!isMobile)
    // An infinite feed mounts one of these per row (and one per 6 tiles inside a
    // row), and every slot it creates stays registered with GAM for the life of
    // the page view. The budget stops the reader's scroll depth from setting how
    // much work each ad refresh does.
    const hasSlot = useAdSlot()

    // Leaving the page takes the tag's GAM slot with it (./ad-slot-reaper);
    // the widget itself never destroys one.
    useEffect(() => {
        setDesktop(wantsDesktopAd(window.innerWidth))
        setMounted(true)
        return () => scheduleAdSlotReap()
    }, [])

    // Hold the tag until the page has stopped producing ads, then let the whole
    // batch insert in one commit. Each insertion starts an auction in the
    // widget, and every auction refreshes EVERY slot on the page and overwrites
    // the previous round's header-bidding targeting — so three mount waves cost
    // three full refreshes and leave the last, cheapest fill on screen. See
    // ./ad-insert-batch for the mechanism.
    const released = useAdInsertRelease(mounted && hasSlot)

    const variant = desktop ? props.bidstream?.desktop : props.bidstream?.mobile
    const meta = extractAdMeta(variant)

    const adsenseStyle = (props.adsense?.props as { style?: CSSProperties } | undefined)?.style
    const adsenseWidth = adsenseStyle
        ? [adsenseStyle.width, adsenseStyle.minWidth, adsenseStyle.maxWidth].find((v) => typeof v === 'number')
        : undefined
    const adsenseHeight = adsenseStyle
        ? [adsenseStyle.height, adsenseStyle.minHeight, adsenseStyle.maxHeight].find((v) => typeof v === 'number')
        : undefined
    const slotWidth = meta?.width ?? adsenseWidth
    const slotHeight = meta?.height ?? adsenseHeight

    const hasBidstream = Boolean(props.bidstream)
    const hasAdsense = Boolean(props.adsense)
    const isBidstreamPartner = Boolean(partner && bidstreamPartnerSet.has(partner))

    // Memoize the rendered element so the JSX/DOM stays referentially stable
    // across parent re-renders. The <sellwild-ad> custom element runs its
    // connectedCallback on every DOM insertion — a stable element keeps it
    // from re-registering a fresh GAM slot (which would also refresh every
    // other slot on the page) on each render.
    const adElement = useMemo(() => {
        // Over budget: render nothing so the space collapses. Reserving a blank
        // 300x250 for an ad that will never be filled is worse than no ad.
        if (mounted && !hasSlot) return null

        // Placeholder until this ad's batch is released, so the space is
        // reserved and the layout does not jump when the tag arrives.
        if (!mounted || !released) {
            return (
                <div
                    className="block mx-auto max-w-full"
                    style={{ width: slotWidth, height: slotHeight }}
                    aria-hidden="true"
                />
            )
        }

        if (hasBidstream && isBidstreamPartner) {
            return desktop ? props.bidstream?.desktop ?? null : props.bidstream?.mobile ?? null
        }

        if (hasAdsense) {
            // React owns the wrapper, not the tag. The widget takes the
            // <sellwild-ad> out of the DOM on its own, so when a parked feed cell
            // later removed the tag React's parent no longer held it: 28 of 32
            // dom-mutation-guard skips from 2026-10-07 to 10-14 were this tag in
            // .browse-ad-cont (d7afbab106c9e3c9). Whatever the widget leaves in
            // the tag's place now leaves with the wrapper.
            //
            // A real box, full width, never `display: contents`: the widget
            // sizes a 728x90 by its PARENT's width and falls back to 320x50 when
            // the parent is narrower (Un() in sellwild-sellwild-next.js). A
            // `contents` box measures 0 wide, so from 2026-10-14 every desktop
            // top banner and product-page banner asked GAM for a 320x50 (seen on
            // prod 2026-10-15: slot sellwild-sellwild-next-desktop-320x50 in a
            // 728x90 box).
            return (
                <div className="block w-full" data-testid="sellwild-ad-wrapper">
                    <sellwild-ad
                        width={slotWidth as number}
                        height={slotHeight as number}
                        className="block mx-auto max-w-full overflow-hidden"
                        style={{ width: slotWidth, height: slotHeight }}
                    />
                </div>
            )
        }

        return null
    }, [mounted, released, hasSlot, hasBidstream, hasAdsense, isBidstreamPartner, slotWidth, slotHeight, desktop])

    if (IS_DEV) return null

    return adElement
}

/**
 * Mounts the ad only once the viewport is known to be wide enough. Gated before
 * `AdFrameworkInner` so a hidden ad never claims a slot from the page budget.
 * Measured once on mount, not on resize: remounting the tag would define a
 * second GAM slot for the same space.
 */
const ViewportGatedAd = (props: AdFrameworkProps & { minViewportWidth: number }) => {
    const [wideEnough, setWideEnough] = useState(false)
    useEffect(() => {
        // Viewport width does not exist during SSR or the hydration render.
        setWideEnough(window.innerWidth >= props.minViewportWidth)
    }, [props.minViewportWidth])
    return wideEnough ? <AdFrameworkInner {...props} /> : null
}

const AdFrameworkOuter = (props: AdFrameworkProps) =>
    props.minViewportWidth
        ? <ViewportGatedAd {...props} minViewportWidth={props.minViewportWidth} />
        : <AdFrameworkInner {...props} />

export const AdFramework = memo(AdFrameworkOuter)

declare module "react" {
    namespace JSX {
      interface IntrinsicElements {
        "sellwild-ad": React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & { width: number; height: number; };
      }
    }
  }
